import {
  reactive,
  ref,
  Ref,
  UnwrapRef,
  onMounted,
  ComponentInternalInstance,
  getCurrentInstance,
  computed,
  ComputedRef,
} from '@vue/composition-api';
import { DialogInstance } from 'tdesign-vue';
import { debounce } from 'lodash';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import store from '@/renderer/store';
import { LOCATION_MAP } from '@/common/reporter/utils';
import CustomDialog from '@/renderer/scripts/utils/CustomDialog';
interface typeOption {
  label: string;
  value: string;
  disabled?: boolean;
}
export interface FilterParam {
  type: 'all' | 'mine';
  uploader: 'all' | 'virus' | 'sensitive-word';
}
const searchUpOptions: typeOption[] = [
  {
    label: i18n_t('全部'),
    value: 'all',
  },
  {
    label: i18n_t('我的'),
    value: 'mine',
  },
];

type TableData = {
  list: any[];
  nextMarker: string;
  selectedRowKeys: Array<string | number>;
  selectedList: SMH.ContentData[];
};
type Handler = {
  tableData: UnwrapRef<TableData>;
  loading: Ref<boolean>;
  loadingOnHeader: Ref<boolean>; // 在顶部loading，loading期间list内容不变
  methods: {
    pullList: (type?: string, scope?: Drawer.FilterData) => void;
    refresh: () => void;
    silentRefresh: () => void;
    onScrollY: (params: { e: WheelEvent }) => void;
    onDelete: (items: SMH.ContentData[]) => void;
    onRestore: (items: SMH.ContentData[]) => void;
  };
  loadingStatus: Ref<string>;
  columns: Array<any>;
  searchTypeOptions: ComputedRef<typeOption[]>;
  searchUpOptions: typeOption[];
  filterParams: UnwrapRef<FilterParam>;
};
export default function separateFileHandler(spaceId?: Ref<SMH.SpaceId>): Handler {
  const currComponent = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const tableData = reactive<TableData>({
    list: [],
    nextMarker: '',
    selectedRowKeys: [],
    selectedList: [],
  });
  const searchTypeOptions = computed(() => {
    let typeOptions: typeOption[] = [];
    if (store.getters.enableManageAntivirus && store.getters.enableSensitiveWord) {
      typeOptions = [
        {
          label: i18n_t('所有'),
          value: 'all',
        },
        {
          label: i18n_t('敏感词'),
          value: 'sensitive-word',
        },
        {
          label: i18n_t('疑似病毒'),
          value: 'virus',
        },
      ];
    }
    return typeOptions;
  });

  const columns: Array<any> = [
    {
      colKey: 'row-select',
      type: 'multiple',
      fixed: 'left',
      width: 20,
      disabled: ({ row }: { row: SMH.ContentData }) => {
        // 若inDirectory为false需禁用
        if (row.inDirectory === false) return true;
        return false;
      },
    },
    {
      colKey: 'name',
      cell: 'name',
      title: i18n_t('文件名'),
      width: 200,
      ellipsis: {
        disable: true,
        content: '',
        hideEmptyPopup: true,
      },
    },
    {
      colKey: 'virusName',
      cell: 'virusName',
      title: i18n_t('隔离原因'),
      width: 200,
      ellipsis: {
        disable: true,
        content: '',
        hideEmptyPopup: true,
      },
    },
    {
      colKey: 'creationTime',
      cell: 'creationTime',
      title: i18n_t('时间'),
      width: 130,
    },
    {
      colKey: 'size',
      title: i18n_t('文件大小'),
      width: 130,
    },
  ];

  const filterParams = reactive<FilterParam>({
    type: 'all',
    uploader: 'all',
  });
  const loading = ref(true);
  const loadingOnHeader = ref(false);
  const loadingStatus = ref('');
  // 记录文件筛选的数据
  const fileScope = ref<Drawer.FilterData>({
    teamScope: [],
    collaborationScope: [],
    personalScope: [],
    selectView: 'mine',
  });

  const methods = {
    modifyScopeData(scope: Drawer.FilterData) {
      const spaceItems: SMH.SpaceItem[] = [];
      spaceItems.push(...scope.teamScope, ...scope.collaborationScope, ...scope.personalScope);
      return spaceItems;
    },
    async pullList(type?: string, scope?: Drawer.FilterData, silentLoading = false) {
      fileScope.value = scope || fileScope.value;
      if (type !== 'scroll') {
        loading.value = !silentLoading;
        loadingOnHeader.value = silentLoading;
      }
      try {
        const spaceItems = methods.modifyScopeData(fileScope.value);
        const opt = {
          limit: 20,
          orderBy: 'creationTime',
          orderByType: 'desc',
          spaceId: spaceId?.value,
          createdBy: filterParams.uploader === 'all' ? '' : store.getters.userId,
          filterType: filterParams.type === 'all' ? '' : filterParams.type,
          spaceItems,
        };
        // 只有当scroll的时候需要增加这个字段并且tableData.nextMarker 不为负数的时候，增加mark字段
        if (type === 'scroll' && tableData.nextMarker) {
          Object.assign(opt, {
            marker: tableData.nextMarker,
          });
        } else {
          Object.assign(opt, {});
        }
        const { data: listRes } = await smh.userAPI.getCloudHerbsList({
          ...opt,
        });
        tableData.nextMarker = listRes?.nextMarker;
        // 只有当滚动的时候需要push
        if (type === 'scroll') {
          tableData.list.push(...listRes.contents);
        } else {
          tableData.list = listRes.contents;
        }
        tableData.list = tableData.list.map((item: any, index: number) => ({
          ...item,
          id: index,
          sensitiveName: i18n_t('内容“{slot1}”触发敏感词', {
            slot1: item.sensitiveWordName,
          }),
        }));
        if (loadingOnHeader.value) {
          tips.success(i18n_t('刷新成功'));
        } else {
          tips.closeAll();
        }
        loading.value = false;
        loadingOnHeader.value = false;
      } catch (error: MsgError) {
        loading.value = false;
        loadingOnHeader.value = false;
        tips.error(error);
      }
    },
    async refresh() {
      tableData.selectedRowKeys = [];
      tableData.selectedList = [];
      await methods.pullList('pull', fileScope.value);
    },
    silentRefresh() {
      if (!loading.value && !loadingOnHeader.value) {
        tableData.selectedRowKeys = [];
        tableData.selectedList = [];
        methods.pullList('pull', fileScope.value, true);
      }
    },
    async onScrollY(params: { e: WheelEvent }) {
      const { target } = params.e;
      const { clientHeight, scrollHeight, scrollTop } = target as HTMLElement;
      if (!tableData.nextMarker) return;
      if (clientHeight + scrollTop >= scrollHeight - 100) {
        loadingStatus.value = 'loading';
        methods.scrollGetData();
      }
    },

    scrollGetData: debounce(async () => {
      await methods.pullList('scroll', fileScope.value);
      loadingStatus.value = '';
    }, 500),
    // 删除的操作
    onDelete(list: SMH.ContentData[]) {
      const deleteDialog: DialogInstance = CustomDialog.confirm({
        header: i18n_t('确定要彻底删除？'),
        body: i18n_t('删除后无法恢复文件'),
        theme: 'warning',
        onConfirm: () => {
          if (deleteDialog.hide) {
            deleteDialog.hide();
          }
          methods.handleDelete(list);
        },
      });
    },
    async handleDelete(list: SMH.ContentData[]) {
      try {
        const { spaceId, spaceOrgId, path } = list[0];
        tips.loading(i18n_t('正在删除文件'));
        const res = await smh.commonAPI.deleteFile({
          filePath: path.join('/'),
          spaceId,
          spaceOrgId: spaceOrgId || store.getters.orgId,
          permanent: 1,
        });
        // TODO 文件的批量删除功能，暂时没有接口提供
        // else {
        //   const batchList: SMH.BatchDeleteItem[] = [];
        //   list.forEach((item: any) => {
        //     if (item.canMarkAsNonVirus) {
        //       const data = { spaceId: item.spaceId, permanent: true, path: item.path.join('/') };
        //       batchList.push(data);
        //     }
        //   });
        //   res = await smh.commonAPI.batchDelete({ batchList });
        // }
        // console.log(res);

        if (res?.status === 200 || res?.status === 204) {
          methods.refresh();
          tips.success(i18n_t('删除成功'));
        }
      } catch (error: MsgError) {
        tips.error(error);
        loading.value = false;
        loadingOnHeader.value = false;
      }
    },
    // 还原的操作
    async onRestore(list: SMH.ContentData[]) {
      const restoreDialog: DialogInstance = CustomDialog.confirm({
        header: i18n_t('是否信任选中文件？'),
        body: i18n_t('确认后文件的可疑标签会被清除，清除后恢复文件操作权限。'),
        theme: 'warning',
        onConfirm: () => {
          if (restoreDialog.hide) {
            restoreDialog.hide();
          }
          methods.handleRestore(list);
        },
      });
    },
    async handleRestore(list: SMH.ContentData[]) {
      try {
        const virusItems: SMH.TrustFileItem[] = [];
        const sensitiveItems: SMH.TrustFileItem[] = [];
        list.forEach((item: any) => {
          const data = {
            spaceId: item.spaceId,
            path: item.path,
          };
          // 根据后端返回的canMarkAsNonVirus和canMarkAsNonSensitiveWord判断是否有权限信任
          // 前端不进行判断是否有权限
          if (item?.canMarkAsNonVirus && item.suspiciousType === 0) {
            virusItems.push(data);
          }
          if (item?.canMarkAsNonSensitiveWord && item.suspiciousType === 1) {
            sensitiveItems.push(data);
          }
        });
        if (virusItems.length > 0 || sensitiveItems.length > 0) {
          tips.loading(i18n_t('正在恢复文件'), 0);
        } else {
          // 选中的文件都无权限进行恢复
          tips.error(i18n_t('恢复失败，选中文件无权限'));
          return;
        }
        let flag = false;
        if (virusItems.length > 0 && sensitiveItems.length > 0) {
          const res = await Promise.all([
            smh.userAPI.trustVirusFile({
              restoreItems: virusItems,
            }),
            smh.userAPI.trustSensitiveFile({
              directoryList: sensitiveItems,
            }),
          ]);
          flag = res[0].status === 204 && res[1].status === 204;
        } else if (virusItems.length > 0 && sensitiveItems.length === 0) {
          const res = await smh.userAPI.trustVirusFile({
            restoreItems: virusItems,
          });
          flag = res.status === 204;
        } else if (sensitiveItems.length > 0 && virusItems.length === 0) {
          const res = await smh.userAPI.trustSensitiveFile({
            directoryList: sensitiveItems,
          });
          flag = res.status === 204;
        }
        sensitiveItems.length > 0 && methods.reporterRestore(list, sensitiveItems);
        if (flag) {
          const trustLength = sensitiveItems.length + virusItems.length;
          await methods.refresh();
          if (list.length === trustLength) {
            tips.success(i18n_t('选中文件已全部恢复'));
          } else {
            tips.error(
              i18n_t('选中{slot1}个文件, {slot2}个文件恢复成功, 其中{slot3}个文件无恢复权限', {
                slot1: list.length,
                slot2: trustLength,
                slot3: list.length - trustLength,
              }),
              3000,
            );
          }
        }
      } catch (error: MsgError) {
        tips.error(i18n_t('选中文件恢复失败'));
      }
    },
    reporterRestore(list: SMH.ContentData[], items: SMH.TrustFileItem[]) {
      const routerName = vm.$route.name || 'teamSpace';
      for (let i = 0; i < list.length; i++) {
        for (let j = 0; j < items.length; j++) {
          if (list[i].path === items[j].path && list[i].spaceId === items[j].spaceId) {
            reporter.report('sensitive_manage', {
              whiten_sensitive_location: LOCATION_MAP[routerName],
              trust_name: list[i].name,
              trust_sensitive_name: list[i].sensitiveWordName,
            });
          }
        }
      }
    },
  };

  return {
    tableData,
    methods,
    loading,
    loadingOnHeader,
    columns,
    loadingStatus,
    searchUpOptions,
    searchTypeOptions,
    filterParams,
  };
}
